import React from 'react';
import styled from 'styled-components';

const StatCard = ({ label, value, icon, bgColor, onClick, style }) => {
  // Function to determine background color based on label or use provided bgColor
  const getBackgroundColor = (label, bgColor) => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    
    if (bgColor) {
      if (isDark) {
        // Convert light colors to dark equivalents
        if (bgColor === '#e6f8f5') return '#1e3a32'; // dark green
        if (bgColor === '#e8f1fd') return '#2c3b56'; // dark blue
        if (bgColor === '#fff3e0') return '#3a3020'; // dark yellow
        if (bgColor === '#ffecf2') return '#3a2a32'; // dark pink
        if (bgColor === '#f0e8fd') return '#2d2a3a'; // dark purple
        if (bgColor === '#ffe6e6') return '#3a2a2a'; // dark red
        return bgColor;
      }
      return bgColor;
    }
    
    const labelText = label.toLowerCase();
    
    if (isDark) {
      switch(labelText) {
        case 'rata-rata nilai semester':
          return '#1e3a32'; // dark green
        case 'jam kersos':
          return '#2c3b56'; // dark blue
        case 'jam lembur':
          return '#3a3020'; // dark yellow
        case 'poin apresiasi':
          return '#3a2a32'; // dark pink
        case 'ketidakhadiran':
          return '#2d2a3a'; // dark purple
        case 'surat peringatan':
          return '#3a2a2a'; // dark red
        default:
          return '#333333'; // Dark gray default
      }
    } else {
      switch(labelText) {
        case 'rata-rata nilai semester':
          return '#e6f8f5'; // soft green
        case 'jam kersos':
          return '#e8f1fd'; // soft blue
        case 'jam lembur':
          return '#fff3e0'; // soft yellow
        case 'poin apresiasi':
          return '#ffecf2'; // soft pink
        case 'ketidakhadiran':
          return '#f0e8fd'; // soft purple
        case 'surat peringatan':
          return '#ffe6e6'; // soft red
        default:
          return '#f5f5f5'; // Default gray
      }
    }
  };

  // Check if this is a multi-line card (Ketidakhadiran)
  const isMultiLineCard = label === 'Ketidakhadiran';
  const isSpAndPoinSikapCard = label === 'Poin Sikap & SP';

  return (
    <StatCardContainer
      style={{ background: getBackgroundColor(label, bgColor), ...style }}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined} // allow keyboard focus if clickable
      role={onClick ? 'button' : undefined} // accessibility
    >
      <CardContent>
        <IconWrapper>{icon}</IconWrapper>
        <StatContent>
          {isMultiLineCard ? (
            <>
              <StatLabel style={{ marginBottom: '10px' }}>{label}</StatLabel>
              <MultiLineList>
                {value.split('\n').map((line, i) => {
                  const [type, days] = line.split(': ');
                  return (
                    <MultiLineItem key={i}>
                      <MultiLineType>{type}</MultiLineType>
                      <MultiLineDays>{days}</MultiLineDays>
                    </MultiLineItem>
                  );
                })}
              </MultiLineList>
            </>
          ) : (
            <>
              <StatValue $small={isSpAndPoinSikapCard}>{value}</StatValue>
              <StatLabel>{label}</StatLabel>
            </>
          )}
        </StatContent>
      </CardContent>
    </StatCardContainer>
  );
};

const StatCardContainer = styled.div`
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s, box-shadow 0.2s;
  border: 1px solid rgba(0, 0, 0, 0.04);
  height: 100%;
  display: flex;
  align-items: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 576px) {
    padding: 0.75rem;
    margin: 0;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  
  @media (max-width: 576px) {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }
`;

const IconWrapper = styled.div`
  font-size: 2rem;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    flex-shrink: 0;
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }
`;

const StatValue = styled.div`
  font-size: ${({ $small }) => ($small ? '1.5rem' : '1.75rem')};
  font-weight: 700;
  color: var(--text-dark);
  line-height: 1;
  white-space: pre-line;
  
  @media (max-width: 576px) {
    font-size: ${({ $small }) => ($small ? '1.2rem' : '1.5rem')};
    margin-bottom: 0.15rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
  margin-top: 0.25rem;
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
    margin-top: 0;
  }
`;

const MultiLineList = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 5px;
  
  @media (max-width: 576px) {
    gap: 8px;
    width: 95%;
  }
`;

const MultiLineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px dashed rgba(0, 0, 0, 0.1);
  gap: 30px; /* Increase spacing between the label and value */
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 576px) {
    flex-direction: row; /* Keep as row even on small screens */
    gap: 10px;
    justify-content: space-between;
    width: 100%;
  }
`;

const MultiLineType = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--text-light);
  
  @media (max-width: 576px) {
    font-size: 0.95rem;
  }
`;

const MultiLineDays = styled.div`
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-dark);
  white-space: nowrap; /* Prevent wrapping on small screens */
  
  @media (max-width: 576px) {
    font-size: 0.95rem;
    text-align: right;
  }
`;

export default StatCard;